import React, { useState, createContext, useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  BrowserNotificationOptions,
  NotificationOptions,
} from '../types/browserNotificationOptions';

export interface BrowserNotificationContextProps {
  showBrowserNotification: (options: BrowserNotificationOptions) => void;
}

export const BrowserNotificationContext =
  createContext<BrowserNotificationContextProps | null>(null);

export const BrowserNotificationProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const [notificationPermission, setNotificationPermission] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const notificationPermissionCheck = async () => {
      let granted = false;
      if ('Notification' in window) {
        if (Notification.permission === 'granted') {
          granted = true;
        } else {
          const permission = await Notification.requestPermission();
          granted = permission === 'granted' ? true : false;
        }
      }
      setNotificationPermission(granted);
    };
    notificationPermissionCheck();
  }, []);

  const showBrowserNotification = (options: BrowserNotificationOptions) => {
    if (notificationPermission) {
      // Play sound if provided
      if (options.audioPath) {
        const audio = new Audio(options.audioPath);
        audio.play();
      }

      const notificationOptions: NotificationOptions = {
        body: options.body,
        icon: options.icon,
        renotify: options.renotify,
        requireInteraction: options.requireInteraction,
        tag: options.tag,
      };

      const notification = new Notification(options.title, notificationOptions);
      notification.onclick = () => {
        if (options.navigateUrl) {
          navigate(options.navigateUrl);
        }
        if (options.callback) {
          options.callback();
        }
        notification.close();
        window.parent.focus();
        return null;
      };
    }
  };

  return (
    <BrowserNotificationContext.Provider value={{ showBrowserNotification }}>
      {children}
    </BrowserNotificationContext.Provider>
  );
};

export function useBrowserNotification(): BrowserNotificationContextProps {
  const context = useContext(BrowserNotificationContext);
  return context || assertNever();
}

function assertNever(): never {
  throw Error(
    'Cannot use useBrowserNotification outside of BrowserNotificationProvider',
  );
}
